import { FC } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import type { MyBookingsQuery, MyTripsQuery } from '../../../gql/graphql';

type Booking = MyBookingsQuery['myBookings'][0];
type Trip = MyTripsQuery['myTrips'][0];

interface RecentActivityProps {
  bookings: Booking[];
  trips: Trip[];
  maxItems?: number;
}

const RecentActivity: FC<RecentActivityProps> = ({
  bookings,
  trips,
  maxItems = 10,
}) => {
  // Combine bookings and trips, sort by date (most recent first)
  const activities: Array<{
    type: 'booking' | 'trip';
    id: number;
    date: string;
    title: string;
    subtitle: string;
    linkTo: string;
  }> = [
    ...bookings.map(booking => ({
      type: 'booking' as const,
      id: booking.bookingId,
      date: booking.embarkationDate,
      title: `${booking.boatName} - ${booking.routeName}`,
      subtitle: `Booking #${booking.bookingId} • ${booking.guestCount} guests`,
      linkTo: `/partner/bookings/${booking.bookingId}`,
    })),
    ...trips.map(trip => ({
      type: 'trip' as const,
      id: trip.tripId,
      date: trip.embarkationDate,
      title: `${trip.boatName || 'Unknown'} - ${trip.routeName || 'Unknown'}`,
      subtitle: `Trip #${trip.tripId}${trip.diveCount ? ` • ${trip.diveCount} dives` : ''}`,
      linkTo: `/partner/trips/${trip.tripId}`,
    })),
  ]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, maxItems);

  if (activities.length === 0) {
    return (
      <div className="rounded-lg bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-xl font-semibold text-gray-900">
          Recent Activity
        </h2>
        <div className="py-8 text-center">
          <p className="text-gray-500">No recent activity</p>
          <p className="mt-1 text-sm text-gray-400">
            Your bookings and trips will appear here
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <h2 className="mb-4 text-xl font-semibold text-gray-900">
        Recent Activity
      </h2>
      <div className="space-y-3">
        {activities.map(activity => (
          <Link
            key={`${activity.type}-${activity.id}`}
            to={activity.linkTo}
            className="block rounded-lg border border-gray-200 p-4 transition-all hover:border-blue-500 hover:bg-blue-50"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="font-semibold text-gray-900">
                  {activity.title}
                </div>
                <div className="mt-1 text-sm text-gray-600">
                  {activity.subtitle}
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  {format(new Date(activity.date), 'MMM dd, yyyy')}
                </div>
              </div>
              <div className="ml-4">
                <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                  {activity.type === 'booking' ? 'Booking' : 'Trip'}
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
      {(bookings.length > 0 || trips.length > 0) && (
        <div className="mt-4 flex justify-center gap-4 text-center">
          {bookings.length > 0 && (
            <Link
              to="/partner/bookings"
              className="text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              View all bookings →
            </Link>
          )}
          {trips.length > 0 && (
            <Link
              to="/partner/trips"
              className="text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              View all trips →
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default RecentActivity;
